import { useState } from "react";
import { Button } from "@mui/material";
import { ConfirmDialog, Drawer, Stepper } from "@outpave/ds";
import UploadReceiptReview from "./UploadReceiptReview";
import { useTranslation } from "react-i18next";
import FileUploadPage from "./FileUploadPage";
import ReceiptScanning from "./ReceiptScanning";
import PostUploadPage, { type ReceiptUploadResult } from "./PostUploadPage";

interface UploadReceiptDrawerProps {
  open: boolean;
  onClose: () => void;
  refetchReceipts?: () => void;
}

// Four screens, three steps: scanning and the summary it produces are both the
// last one.
const STEP_KEYS = ["File Upload", "Review", "Summary"];

enum UploadReceiptSteps {
  FILE_UPLOAD,
  REVIEW,
  UPLOADING,
  COMPLETED,
}

const UploadReceiptDrawer: React.FC<UploadReceiptDrawerProps> = ({
  open,
  onClose,
  refetchReceipts,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(UploadReceiptSteps.FILE_UPLOAD);
  const [files, setFiles] = useState<File[]>([]);
  // What the pending unsaved-changes confirm will do once accepted: close the
  // whole drawer, or step back to file selection (both discard the selection).
  const [pendingLeave, setPendingLeave] = useState<"close" | "back" | null>(
    null,
  );

  const [uploadResult, setUploadResult] = useState<ReceiptUploadResult | null>(
    null,
  );

  const handleAddFiles = (fileList: FileList) => {
    // Picking a file that is already listed would upload it as a second receipt.
    setFiles((prev) => [
      ...prev,
      ...Array.from(fileList).filter(
        (file) =>
          !prev.some(
            (kept) =>
              kept.name === file.name &&
              kept.size === file.size &&
              kept.lastModified === file.lastModified,
          ),
      ),
    ]);
    if (step === UploadReceiptSteps.FILE_UPLOAD)
      setStep(UploadReceiptSteps.REVIEW);
    return Promise.resolve();
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    // Only warn about "unsaved changes" while the user is actively selecting/reviewing
    // files (before upload completes). Once upload is completed, there is nothing to lose.
    if (
      step !== UploadReceiptSteps.FILE_UPLOAD &&
      step !== UploadReceiptSteps.COMPLETED
    ) {
      setPendingLeave("close");
    } else {
      // The receipts are already uploaded, so any dismissal of the summary refreshes the library.
      if (step === UploadReceiptSteps.COMPLETED) refetchReceipts?.();
      onClose();
    }
  };

  const handleConfirmLeave = () => {
    if (pendingLeave === "back") {
      setStep(UploadReceiptSteps.FILE_UPLOAD);
      setFiles([]);
      setPendingLeave(null);
      return;
    }
    // Files that finished uploading before leaving are already in the library.
    if (step === UploadReceiptSteps.UPLOADING) refetchReceipts?.();
    setPendingLeave(null);
    onClose();
  };

  const handleReset = () => {
    setStep(UploadReceiptSteps.FILE_UPLOAD);
    setFiles([]);
    setUploadResult(null);
  };

  const renderContent = () => {
    switch (step) {
      case UploadReceiptSteps.FILE_UPLOAD:
        return <FileUploadPage onFilesAdded={handleAddFiles} />;
      case UploadReceiptSteps.REVIEW:
        return (
          <UploadReceiptReview
            files={files}
            onRemoveFile={handleRemoveFile}
            onAddFiles={handleAddFiles}
          />
        );
      case UploadReceiptSteps.UPLOADING:
        return (
          <ReceiptScanning
            files={files}
            onComplete={(result) => {
              setUploadResult(result);
              setStep(UploadReceiptSteps.COMPLETED);
            }}
          />
        );
      case UploadReceiptSteps.COMPLETED:
        return uploadResult ? (
          <PostUploadPage uploadResult={uploadResult} />
        ) : null;
      default:
        return null;
    }
  };

  const renderFooter = () => {
    switch (step) {
      case UploadReceiptSteps.FILE_UPLOAD:
        return (
          <Button variant="primary-outlined" onClick={handleClose}>
            {t("Cancel")}
          </Button>
        );
      case UploadReceiptSteps.REVIEW:
        return (
          <>
            <Button
              variant="primary-outlined"
              onClick={() => setPendingLeave("back")}
            >
              {t("Back")}
            </Button>
            <Button
              variant="primary-filled"
              onClick={() => setStep(UploadReceiptSteps.UPLOADING)}
              disabled={files.length === 0}
            >
              {t("Next")}
            </Button>
          </>
        );
      case UploadReceiptSteps.COMPLETED:
        return (
          <Button
            variant="primary-filled"
            onClick={() => {
              onClose();
              refetchReceipts?.();
            }}
          >
            {t("Confirm")}
          </Button>
        );
      // Scanning offers no action of its own; leaving still goes through the
      // unsaved-changes warning on the header X.
      case UploadReceiptSteps.UPLOADING:
      default:
        return undefined;
    }
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        onAfterClose={handleReset}
        title={t("Upload Receipt")}
        footer={renderFooter()}
      >
        <Stepper
          activeStep={Math.min(step, STEP_KEYS.length - 1)}
          steps={STEP_KEYS.map((key) => ({ id: key, label: t(key) }))}
        />

        {renderContent()}
      </Drawer>

      <ConfirmDialog
        open={pendingLeave !== null}
        title={t("Unsaved Changes")}
        message={t("You have unsaved changes, are you sure you want to leave?")}
        confirmLabel={t("Leave")}
        cancelLabel={t("Cancel")}
        onCancel={() => setPendingLeave(null)}
        onConfirm={handleConfirmLeave}
      />
    </>
  );
};

export default UploadReceiptDrawer;
